/**
 * IsolationSection — §2.2 «ТУСГААРЛАЛТ ХИЙХ ТОНОГ ТӨХӨӨРӨМЖ».
 *
 * Маягтын §2.2: «Цахилгаан схемийг задалж, биркийг авч, пайз, цоож зүүх,
 * тусгаарлалт хийх тоног төхөөрөмжүүд».
 *
 * ⚠ ЯАГААД ЛАВЛАХААС СОНГОДОГ БОЛОВ:
 *   Урьд нь энэ хэсэг 2000 тэмдэгтийн ЧӨЛӨӨТ ТЕКСТ (`isolation_note`)
 *   байсан. Үр дагавар:
 *     • бичих эсэх нь бөглөгчөөс хамаарна — ихэвчлэн хоосон
 *     • хэвлэхэд текст хоосон бол АЖИЛ ХИЙГДЭХ бүх ТТ-г цувуулж
 *       тавьдаг байв. Гэтэл тусгаарлалт хийх ТТ нь ӨӨР төхөөрөмж —
 *       ихэвчлэн тэжээлийн шүүгээ, таслуур. Буруу мэдээлэл нь хоосноос
 *       ДОР: ажилтан буруу төхөөрөмж дээр цоож зүүж болно.
 *     • «аль таслуурыг хэдэн удаа тусгаарласан» гэдгийг тоолох боломжгүй
 *
 * ⚠ Мөр бүрд ТОДРУУЛГА бичнэ («таслуур №3-ыг салгаж цоожлов»). Цоож,
 *   пайзны дугаарыг энд бичих нь §2.2-ын гол утга.
 *
 * ⚠ Наряд хадгалагдсаны дараа л ажиллана — мөр нь `job_id`-д холбогдоно.
 *
 * Backend: /xab/hr-job-isolation (GET · PUT)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Select, Tooltip, message } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";

import { getJobIsolation, setJobIsolation } from "src/api/high_risk";
import { xabHrRefApi } from "src/api/xabApi";

const PRIMARY = "#0F6E56";

export default function IsolationSection({
  jobId,
  unitCode,
  readonly,
  /** Хуучин наряд дээрх чөлөөт текст — түүхэн баримт тул харуулна */
  legacyNote,
}) {
  const [rows, setRows] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    if (!jobId) return;
    setLoading(true);
    getJobIsolation(jobId)
      .then((d) => {
        setRows(d?.items || []);
        setUnavailable(!!d?.unavailable);
        setDirty(false);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  // Тоног төхөөрөмжийн лавлах — БН сонгосон бол тухайн цехийнхээр шүүнэ
  const fetchDevices = useCallback(
    (q) => {
      xabHrRefApi
        .devices({
          q: q || undefined,
          unit_code: unitCode || undefined,
          limit: 300,
        })
        .then((r) => setDevices(r?.items || []))
        .catch(() => setDevices([]));
    },
    [unitCode],
  );

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const options = useMemo(
    () =>
      devices.map((o) => ({
        value: String(o.legacy_id),
        label: o.unit_code ? `${o.title} — ${o.unit_code}` : o.title,
      })),
    [devices],
  );

  const addRow = () => {
    setRows((p) => [...p, { device_legacy_id: null, device_name: "", note: "" }]);
    setDirty(true);
  };

  const patch = (i, next) => {
    setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...next } : r)));
    setDirty(true);
  };

  const drop = (i) => {
    setRows((p) => p.filter((_r, idx) => idx !== i));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const d = await setJobIsolation({ job_id: jobId, items: rows });
      setRows(d?.items || []);
      setDirty(false);
      message.success("Тусгаарлалтын жагсаалт хадгалагдлаа");
    } catch (e) {
      // ⚠ Бааз руу бичигдээгүйг ил гаргана. Чимээгүй өнгөрвөл наряд
      //   хэвлэхэд §2.2 хоосон гарч, ажилтан юуг тусгаарлахаа мэдэхгүй.
      message.error(
        e?.response?.data?.message || "Хадгалж чадсангүй — дахин оролдоно уу",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!jobId) {
    return (
      <div style={{ fontSize: 11.5, color: "#A8AFBC" }}>
        Нарядыг хадгалсны дараа тусгаарлалт хийх ТТ-г сонгоно.
      </div>
    );
  }

  if (unavailable) {
    return (
      <div>
        <Alert
          type="warning"
          showIcon
          style={{ fontSize: 12, marginBottom: 6 }}
          message="Тусгаарлалтын хүснэгт бэлэн болоогүй"
          description={
            <span>
              <code>create_hr_job_isolation.sql</code> миграцыг ажиллуулна уу.
            </span>
          }
        />
        {legacyNote ? (
          <div style={{ fontSize: 11.5, color: "#5B6472" }}>{legacyNote}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Хуучин наряд дээрх чөлөөт текст — түүхэн баримт */}
      {legacyNote && rows.length === 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ fontSize: 11.5, marginBottom: 6 }}
          message="Өмнө нь бичсэн тэмдэглэл"
          description={legacyNote}
        />
      ) : null}

      {rows.length === 0 && !loading ? (
        <div style={{ fontSize: 11.5, color: "#A8AFBC", marginBottom: 4 }}>
          Тусгаарлалт хийх ТТ сонгоогүй байна.
        </div>
      ) : null}

      {rows.map((r, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <Select
            showSearch
            allowClear
            size="small"
            disabled={readonly}
            style={{ flex: 1, minWidth: 0 }}
            placeholder="Тусгаарлах ТТ (тэжээлийн шүүгээ, таслуур…)"
            filterOption={false}
            onSearch={fetchDevices}
            value={
              r.device_legacy_id != null
                ? String(r.device_legacy_id)
                : r.device_name || undefined
            }
            options={options}
            onChange={(v) => {
              const o = devices.find((x) => String(x.legacy_id) === String(v));
              patch(i, {
                device_legacy_id: v == null ? null : Number(v),
                device_name: o?.title || "",
              });
            }}
          />
          <Input
            size="small"
            disabled={readonly}
            style={{ flex: 1 }}
            placeholder="Цоож/пайзны дугаар, тодруулга"
            value={r.note || ""}
            onChange={(e) => patch(i, { note: e.target.value })}
          />
          {!readonly ? (
            <Tooltip title="Хасах">
              <CloseOutlined
                onClick={() => drop(i)}
                style={{ color: "#C4C9D1", cursor: "pointer", fontSize: 11 }}
              />
            </Tooltip>
          ) : null}
        </div>
      ))}

      {!readonly ? (
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={addRow}>
            ТТ нэмэх
          </Button>
          {/* ⚠ Хадгалах товч ТУСДАА: энэ хэсэг нарядын үндсэн маягтаас
              ӨӨР endpoint руу бичдэг тул «Хадгалах» дарахад автоматаар
              хамт явахгүй. Товчгүй бол өөрчлөлт чимээгүй алдагдана. */}
          {dirty ? (
            <Button
              size="small"
              type="primary"
              loading={saving}
              style={{ background: PRIMARY, borderColor: PRIMARY }}
              onClick={save}
            >
              Хадгалах
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
